import { DateTime, Duration, Interval } from 'luxon';
import { useEffect, useState } from 'react';
import './product-calender.scss';
import '@fortawesome/fontawesome-free/css/all.css';
import { DateTimePicker } from '../../../components/date-picker/date-time-picker';
import {
  DailyHours,
  DateTimeInterval,
  Location,
  mergeDateTimeIntervals,
  TimeInterval,
} from '../../../clients/quipli-service/location/location';
import { UnitsAvailable } from './units-available/units-available';
import { CustomerProduct } from '../../../clients/customer-api/product/customer-product';
import {
  ceilToStep,
  findNextOpenTimeAfter,
  findPreviousCloseTime,
  findPreviousOpenTimeBefore,
  parseIsoTime,
} from '../../../util/date-time-util';
import { useCustomerAPI } from '../../../clients/customer-api/customer-api-client';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { ProductTrackingType } from '../../../clients/quipli-service/product/product';

const defaultStep = Duration.fromObject({ minutes: 30 });
const DISTANT_FUTURE_DATETIME = DateTime.now().plus({ years: 1000 });

export const ProductCalendar = ({
  product,
  quantity,
  location,
  step = defaultStep,
  setRootAttributes, // TODO: remove all usage of setRootAttributes
  setRentalDates,
}: {
  product: CustomerProduct;
  quantity: number;
  location: Location;
  step?: Duration;
  setRootAttributes?: (o: any) => void;
  setRentalDates?: (dates: { start?: DateTime; end?: DateTime }) => void;
}) => {
  const { getRentalAvailability } = useCustomerAPI();
  const [startDateTime, setStartDateTime] = useState<DateTime | undefined>();
  const [endDateTime, setEndDateTime] = useState<DateTime | undefined>();
  const [startDaysHours, setStartDaysHours] = useState<TimeInterval | undefined>();
  const [endDaysHours, setEndDaysHours] = useState<TimeInterval | undefined>();
  const [availability, setAvailability] = useState<DateTimeInterval[]>();
  const [disabledStartIntervals, setDisabledStartIntervals] = useState<Interval[]>();
  const [disabledEndIntervals, setDisabledEndIntervals] = useState<Interval[]>([]);
  const [correctedHoursOfOperation, setCorrectedHoursOfOperation] = useState<DailyHours[]>([]);
  const [disabledDaysOfWeek, setDisabledDaysOfWeek] = useState<number[]>([]);
  const rentalTimeSlots = product.rentalTimeSlots.length ? product.rentalTimeSlots : location.rentalTimeSlots;

  useEffect(() => {
    if (setRootAttributes) {
      setRootAttributes({
        'data-start-date-time': startDateTime?.toISO() || '',
        'data-end-date-time': endDateTime?.toISO() || '',
      });
    }
    if (setRentalDates) {
      setRentalDates({ start: startDateTime, end: endDateTime });
    }
  }, [setRootAttributes, setRentalDates, startDateTime, endDateTime]);

  useEffect(() => {
    if (product.maxRentalDuration) {
      const checkValidReturnTime = (start: DateTime) => {
        const minReturnTime = start.plus(product.minRentalDuration);
        const maxReturnTime = start.plus(product.maxRentalDuration!);
        return (
          findNextOpenTimeAfter(
            location.hoursOfOperation,
            location.disabledDates,
            'rentalEnd',
            minReturnTime,
            rentalTimeSlots.map((ts) => ts.end),
          ).diff(maxReturnTime).milliseconds <= 0
        );
      };

      const correctedHours = location.hoursOfOperation
        .sort((a, b) => b.numericDayOfWeek - a.numericDayOfWeek)
        .map((current) => {
          let rentalStart = DateTime.local({ zone: location.timezone })
            .startOf('minute')
            .set({ weekday: current.numericDayOfWeek, ...parseIsoTime(current.rentalStart.start) });
          const rentalStartClose = rentalStart.set(parseIsoTime(current.rentalStart.end));
          while (rentalStart.diff(rentalStartClose).milliseconds < 0) {
            if (!checkValidReturnTime(rentalStart)) {
              rentalStart = rentalStart.minus(step);
              break;
            }
            rentalStart = rentalStart.plus(step);
          }

          return {
            ...current,
            rentalStart: {
              start: current.rentalStart.start,
              end: rentalStart.toLocaleString(DateTime.TIME_24_SIMPLE),
            },
          };
        });
      setCorrectedHoursOfOperation(correctedHours);
    } else {
      setCorrectedHoursOfOperation(location.hoursOfOperation);
    }
  }, [product, location, step]);

  useEffect(() => {
    let active = true;
    if (correctedHoursOfOperation.length !== 0) {
      const refreshAvailability = async () => {
        const availability = await getRentalAvailability(product.id, location.id, quantity);
        if (active) {
          setAvailability(availability);
          const disabledIntervals: Interval[] = [
            Interval.fromDateTimes(
              DateTime.fromSeconds(0),
              findNextOpenTimeAfter(
                correctedHoursOfOperation,
                location.disabledDates,
                'rentalStart',
                DateTime.local({ zone: location.timezone }).plus(location.minimumLeadTime),
                rentalTimeSlots.map((ts) => ts.start),
              ),
            ),
            Interval.fromDateTimes(
              findPreviousOpenTimeBefore(
                correctedHoursOfOperation,
                location.disabledDates,
                'rentalStart',
                DateTime.local({ zone: location.timezone }).plus(location.maximumLeadTime),
              ),
              DISTANT_FUTURE_DATETIME.setZone(location.timezone),
            ),
          ];

          let previousEnd: DateTime | undefined = DateTime.fromSeconds(0).setZone(location.timezone);
          const merged = mergeDateTimeIntervals(
            availability.filter(
              (i) => !(i.start && i.end && i.end.diff(i.start).milliseconds < product.minRentalDuration.toMillis()),
            ),
          );
          merged.forEach((av) => {
            const start = previousEnd || DateTime.fromSeconds(0);
            const end = av.start || DateTime.local({ zone: location.timezone });
            disabledIntervals.push(Interval.fromDateTimes(start, end));
            previousEnd = av.end;
          });

          if (previousEnd !== undefined) {
            disabledIntervals.push(
              Interval.fromDateTimes(previousEnd, DISTANT_FUTURE_DATETIME.setZone(location.timezone)),
            );
          }

          const correctedForHoursOfOperation = Interval.merge(disabledIntervals).map((i) => {
            let start = i.start;
            const end = i.end;
            start = findPreviousOpenTimeBefore(
              correctedHoursOfOperation,
              location.disabledDates,
              'rentalStart',
              start.minus(product.minRentalDuration),
              rentalTimeSlots.map((ts) => ts.start),
            );

            return Interval.fromDateTimes(
              start,
              findNextOpenTimeAfter(
                correctedHoursOfOperation,
                location.disabledDates,
                'rentalStart',
                end,
                rentalTimeSlots.map((ts) => ts.start),
              ),
            );
          });
          const disabledDayIntervals = location.disabledDates.map((dd) =>
            Interval.fromDateTimes(dd.startOf('day').minus(1), dd.endOf('day').plus(1)),
          );
          const intervals = Interval.merge([...correctedForHoursOfOperation, ...disabledDayIntervals]);
          setDisabledStartIntervals(intervals);
        }
      };

      if (location.checkAvailability && product.trackingType !== ProductTrackingType.NonInventory) {
        refreshAvailability();
      } else {
        const disabledDayIntervals = location.disabledDates.map((dd) =>
          Interval.fromDateTimes(dd.startOf('day').minus(1), dd.endOf('day').plus(1)),
        );
        const productDisabledInterval =
          product.disabledStartDate || product.disabledEndDate
            ? [
                Interval.fromDateTimes(
                  product.disabledStartDate?.minus(1) || DateTime.fromSeconds(0, { zone: location.timezone }),
                  product.disabledEndDate?.endOf('day').plus(1) || DISTANT_FUTURE_DATETIME.setZone(location.timezone),
                ),
              ]
            : [];
        setDisabledStartIntervals(
          Interval.merge([
            ...disabledDayIntervals,
            ...productDisabledInterval,
            Interval.fromDateTimes(
              DateTime.fromSeconds(0),
              findNextOpenTimeAfter(
                correctedHoursOfOperation,
                location.disabledDates,
                'rentalStart',
                DateTime.local({ zone: location.timezone }).plus(location.minimumLeadTime),
                rentalTimeSlots.map((ts) => ts.start),
              ),
            ),
            Interval.fromDateTimes(
              findPreviousOpenTimeBefore(
                correctedHoursOfOperation,
                location.disabledDates,
                'rentalStart',
                DateTime.local({ zone: location.timezone }).plus(location.maximumLeadTime),
              ),
              DISTANT_FUTURE_DATETIME.setZone(location.timezone),
            ),
          ]),
        );
      }
    }
    return () => {
      active = false;
    };
  }, [product, location, correctedHoursOfOperation, quantity]);

  useEffect(() => {
    if (location.checkAvailability && product.trackingType !== ProductTrackingType.NonInventory) {
      if (startDateTime?.isValid && availability && product) {
        const currentAvailabilityInterval = availability.reverse().find((av) => {
          const startsBefore = av.start === undefined || av.start.diff(startDateTime).milliseconds < 0;
          const endsAfter = av.end === undefined || av.end.diff(startDateTime).milliseconds > 0;
          return startsBefore && endsAfter;
        });

        let availabilityEnd = currentAvailabilityInterval?.end?.plus(1);
        if (
          product.maxRentalDuration &&
          (availabilityEnd === undefined ||
            availabilityEnd.diff(startDateTime.plus(product.maxRentalDuration)).milliseconds > 0)
        ) {
          availabilityEnd = startDateTime.plus(product.maxRentalDuration).plus(1);
        }
        const intervals = [
          Interval.fromDateTimes(
            DateTime.fromSeconds(0, { zone: location.timezone }),
            findNextOpenTimeAfter(
              correctedHoursOfOperation,
              location.disabledDates,
              'rentalEnd',
              startDateTime.plus(product.minRentalDuration).minus(1),
              rentalTimeSlots.map((ts) => ts.end),
            ),
          ),
        ];
        if (availabilityEnd) {
          intervals.push(
            Interval.fromDateTimes(
              findPreviousOpenTimeBefore(
                correctedHoursOfOperation,
                location.disabledDates,
                'rentalEnd',
                availabilityEnd,
              ).plus(1),
              DISTANT_FUTURE_DATETIME.setZone(location.timezone),
            ),
          );
        }

        const disabledDayIntervals = location.disabledDates.map((dd) =>
          Interval.fromDateTimes(dd.startOf('day').minus(1), dd.endOf('day').plus(1)),
        );
        setDisabledEndIntervals(Interval.merge([...intervals, ...disabledDayIntervals]));
      }
    } else {
      if (startDateTime?.isValid && product) {
        const intervals = [
          Interval.fromDateTimes(
            DateTime.fromSeconds(0, { zone: location.timezone }),
            findNextOpenTimeAfter(
              correctedHoursOfOperation,
              location.disabledDates,
              'rentalEnd',
              startDateTime.plus(product.minRentalDuration).minus(1),
              rentalTimeSlots.map((ts) => ts.end),
            ),
          ),
        ];
        if (product.maxRentalDuration) {
          const end = findPreviousOpenTimeBefore(
            correctedHoursOfOperation,
            location.disabledDates,
            'rentalEnd',
            startDateTime.plus(product.maxRentalDuration),
          );
          intervals.push(Interval.fromDateTimes(end.plus(1), DISTANT_FUTURE_DATETIME.setZone(location.timezone)));
        }

        if (product.disabledStartDate && product.disabledStartDate > startDateTime) {
          intervals.push(
            Interval.fromDateTimes(
              product.disabledStartDate.minus(1),
              DISTANT_FUTURE_DATETIME.setZone(location.timezone),
            ),
          );
        }

        const disabledDayIntervals = location.disabledDates.map((dd) =>
          Interval.fromDateTimes(dd.startOf('day').minus(1), dd.endOf('day').plus(1)),
        );
        setDisabledEndIntervals(Interval.merge([...intervals, ...disabledDayIntervals]));
      }
    }
  }, [startDateTime, availability, product, correctedHoursOfOperation, location]);

  useEffect(() => {
    const disabledDaysOfWeek = Array.from(Array(7).keys())
      .map((i) => i + 1)
      .filter((i) => !location.hoursOfOperation.find((ho) => ho.numericDayOfWeek === i));
    setDisabledDaysOfWeek(disabledDaysOfWeek);
  }, [location]);

  //initialize endDateTime
  useEffect(() => {
    if (startDateTime?.isValid) {
      if (location.showRentalTimeSelect) {
        if (!endDateTime || startDateTime.diff(endDateTime).milliseconds > 0) {
          if (
            product?.maxRentalDuration &&
            product.maxRentalDuration.as('milliseconds') < location.defaultRentalDuration.as('milliseconds')
          ) {
            setEndDateTime(
              findPreviousOpenTimeBefore(
                correctedHoursOfOperation,
                location.disabledDates,
                'rentalEnd',
                startDateTime.plus(product.maxRentalDuration),
                rentalTimeSlots.map((ts) => ts.end),
              ),
            );
          } else {
            let defaultDuration = location.defaultRentalDuration;
            if (location.defaultRentalDuration.as('milliseconds') < product.minRentalDuration.as('milliseconds')) {
              defaultDuration = product.minRentalDuration;
            }
            let defaultEndTime = startDateTime.plus(defaultDuration);
            if (rentalTimeSlots.length) {
              const endSlots = rentalTimeSlots.map((ts) => ts.end);
              let endSlot = endSlots
                .map((t) => defaultEndTime.set(parseIsoTime(t)))
                .find((e) => e.diff(defaultEndTime).milliseconds >= 0);
              if (!endSlot) {
                endSlot = endSlots
                  .map((t) => startDateTime.set(parseIsoTime(t)))
                  .find((e) => e.diff(startDateTime).milliseconds >= 0);
              }
              if (!endSlot) {
                endSlot = startDateTime.plus({ days: 1 }).set(parseIsoTime(endSlots[0]));
              }
              defaultEndTime = endSlot;
            }

            const previousOpenTime = findPreviousOpenTimeBefore(
              correctedHoursOfOperation,
              location.disabledDates,
              'rentalEnd',
              defaultEndTime,
              rentalTimeSlots.map((ts) => ts.end),
            );
            if (previousOpenTime.diff(startDateTime).milliseconds >= product.minRentalDuration.as('milliseconds')) {
              setEndDateTime(previousOpenTime);
            } else {
              setEndDateTime(
                findNextOpenTimeAfter(
                  correctedHoursOfOperation,
                  location.disabledDates,
                  'rentalEnd',
                  defaultEndTime,
                  rentalTimeSlots.map((ts) => ts.end),
                ),
              );
            }
          }
        }
      } else {
        if (!endDateTime || startDateTime.diff(endDateTime).milliseconds > 0) {
          let defaultRentalDuration = location.defaultRentalDuration;
          let isMax = false;
          if (
            product?.maxRentalDuration &&
            product.maxRentalDuration.as('milliseconds') < location.defaultRentalDuration.as('milliseconds')
          ) {
            defaultRentalDuration = product.maxRentalDuration;
            isMax = true;
          } else if (product.minRentalDuration.as('milliseconds') > location.defaultRentalDuration.as('milliseconds')) {
            defaultRentalDuration = product.minRentalDuration;
          }
          if (isMax) {
            setEndDateTime(
              findPreviousCloseTime(
                correctedHoursOfOperation,
                location.disabledDates,
                'rentalEnd',
                startDateTime.plus(defaultRentalDuration),
              ),
            );
          } else {
            const defaultEndTime = findPreviousCloseTime(
              correctedHoursOfOperation,
              location.disabledDates,
              'rentalEnd',
              findNextOpenTimeAfter(
                correctedHoursOfOperation,
                location.disabledDates,
                'rentalEnd',
                startDateTime.plus(defaultRentalDuration),
                rentalTimeSlots.map((ts) => ts.end),
              ).endOf('day'),
            );
            setEndDateTime(defaultEndTime);
          }
        } else {
          const closeTime = findPreviousCloseTime(
            correctedHoursOfOperation,
            location.disabledDates,
            'rentalEnd',
            endDateTime.endOf('day'),
          );
          if (closeTime.diff(endDateTime).milliseconds !== 0) {
            setEndDateTime(closeTime);
          }
        }
      }
    }
  }, [startDateTime, endDateTime, location, product, disabledEndIntervals, correctedHoursOfOperation, step]);

  useEffect(() => {
    if (endDateTime) {
      const hours = correctedHoursOfOperation.find((ho) => ho.numericDayOfWeek === endDateTime.weekday);
      setEndDaysHours(hours?.rentalEnd);
    }
  }, [endDateTime, location, correctedHoursOfOperation]);

  useEffect(() => {
    if (startDateTime?.isValid) {
      const hours = correctedHoursOfOperation.find((ho) => ho.numericDayOfWeek === startDateTime.weekday);
      if (hours && product) {
        setStartDaysHours(hours.rentalStart);
        const min = startDateTime.set(parseIsoTime(hours.rentalStart.start));
        if (!location.showRentalTimeSelect) {
          const sortedDisabledEnds = (disabledStartIntervals || [])
            ?.filter(
              (i) =>
                i.end.startOf('day').diff(min.startOf('day')).milliseconds === 0 &&
                i.start.diff(min).milliseconds < 0 &&
                i.end.diff(min).milliseconds > 0,
            )
            .map((i) => i.end)
            .sort((a, b) => b.diff(a).milliseconds);
          const correctedStart =
            sortedDisabledEnds.length > 0
              ? findNextOpenTimeAfter(
                  correctedHoursOfOperation,
                  location.disabledDates,
                  'rentalStart',
                  ceilToStep(sortedDisabledEnds[0], step),
                  rentalTimeSlots.map((ts) => ts.start),
                )
              : min;
          if (correctedStart.diff(startDateTime).milliseconds !== 0) {
            setStartDateTime(correctedStart);
          }
        }
      }
    } else if (!startDateTime && disabledStartIntervals) {
      const earliestDefaultStart =
        location.defaultLeadTime.as('milliseconds') > location.minimumLeadTime.as('milliseconds')
          ? DateTime.local({ zone: location.timezone }).plus(location.defaultLeadTime)
          : DateTime.local({ zone: location.timezone }).plus(location.minimumLeadTime);
      const sortedDisabledEnds = disabledStartIntervals
        ?.filter((i) => i.start.diff(earliestDefaultStart).milliseconds < 0)
        .map((i) => i.end)
        .sort((a, b) => a.diff(b).milliseconds);

      if (sortedDisabledEnds.length === 0 || sortedDisabledEnds[0].diff(earliestDefaultStart).milliseconds < 0) {
        setStartDateTime(
          findNextOpenTimeAfter(
            correctedHoursOfOperation,
            location.disabledDates,
            'rentalStart',
            ceilToStep(earliestDefaultStart, step),
            rentalTimeSlots.map((ts) => ts.start),
          ),
        );
      } else {
        setStartDateTime(
          findNextOpenTimeAfter(
            correctedHoursOfOperation,
            location.disabledDates,
            'rentalStart',
            ceilToStep(sortedDisabledEnds[0], step),
            rentalTimeSlots.map((ts) => ts.start),
          ),
        );
      }
    }
  }, [location, correctedHoursOfOperation, startDateTime, step, disabledStartIntervals, product]);

  return (
    <div className="product-calendar">
      <div>
        <label className="mt-1 mb-1">Rent from</label>
        {location.showRentalTimeSelect ? (
          <DateTimePicker
            name="rental_start_date_time"
            value={startDateTime}
            onChange={(v) => setStartDateTime(v)}
            disabledDaysOfWeek={disabledDaysOfWeek}
            disabledIntervals={disabledStartIntervals}
            step={step}
            minTime={startDaysHours?.start}
            maxTime={startDaysHours?.end}
            restrictToTimes={rentalTimeSlots.map((ts) => ts.start)}
            timezone={location.timezone}
          />
        ) : (
          <DatePicker
            name="rental_start_date_time"
            value={startDateTime}
            onChange={(v) => setStartDateTime(v)}
            disabledDaysOfWeek={disabledDaysOfWeek}
            disabledIntervals={disabledStartIntervals}
          />
        )}
      </div>

      <div>
        <label className="mt-2 mb-1">Rent to</label>
        {location.showRentalTimeSelect ? (
          <DateTimePicker
            name="rental_end_date_time"
            value={endDateTime}
            onChange={setEndDateTime}
            disabledDaysOfWeek={disabledDaysOfWeek}
            disabledIntervals={disabledEndIntervals}
            step={step}
            minTime={endDaysHours?.start}
            maxTime={endDaysHours?.end}
            restrictToTimes={rentalTimeSlots.map((ts) => ts.end)}
            timezone={location.timezone}
          />
        ) : (
          <DatePicker
            name="rental_end_date_time"
            value={endDateTime}
            onChange={setEndDateTime}
            disabledDaysOfWeek={disabledDaysOfWeek}
            disabledIntervals={disabledEndIntervals}
          />
        )}
      </div>
      {location.showUnitsAvailable && product.trackingType !== ProductTrackingType.NonInventory && (
        <UnitsAvailable
          productId={product.id}
          locationId={location.id}
          startDateTime={startDateTime}
          endDateTime={endDateTime}
        />
      )}
    </div>
  );
};
